'use client';

import { useState, useEffect } from 'react';
import { useUserStore } from './useUser';
import { shouldEnableAds, AD_SLOTS, shouldShowAdsOnPage } from '../utilities/ads';

/**
 * Custom hook for AdSense management
 * - Provides ad slot configuration
 * - Handles ad density calculation
 * - Tracks viewport for mobile/desktop targeting
 */
export function useAdsense() {
  const { isProMemberActive } = useUserStore();
  const [pathname, setPathname] = useState('');
  const showAds = shouldEnableAds(isProMemberActive) && shouldShowAdsOnPage(pathname);
  const [isMobile, setIsMobile] = useState(false);

  // Get current pathname
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setPathname(window.location.pathname);
    }
  }, []);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkMobile();

    // Listen for resize events
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  /**
   * Calculate optimal ad density for list pages
   * @param {number} itemCount - Number of items in the list
   * @returns {number} - How many items between each ad
   */
  const getAdDensity = (itemCount) => {
    if (!showAds) return 0;

    if (itemCount > 20) return 12;
    if (itemCount > 10) return 6;
    if (itemCount > 5) return 6;
    if (itemCount > 3) return 3;
    return 0;
  };

  /**
   * Track ad impression in analytics
   * @param {string} adSlot - Ad slot identifier
   * @param {string} location - Page location
   */
  const trackAdImpression = (adSlot, location) => {
    if (window.gtag) {
      window.gtag('event', 'ad_impression', {
        ad_slot: adSlot,
        ad_location: location,
      });
    }
  };

  /**
   * Get appropriate ad configuration based on page type
   * @param {string} pageType - Type of page (article, list, tool, etc.)
   * @returns {Object} - Ad configuration for the page
   */
  const getPageAdConfig = (pageType) => {
    const configs = {
      article: {
        showBelowTitle: true,
        showFirstParagraph: true,
        showMidContent: true,
        showEndContent: true,
        showSidebar: !isMobile,
        showStickyMobile: isMobile,
      },
      list: {
        showBelowTitle: false,
        showFirstParagraph: false,
        showMidContent: false,
        showEndContent: true,
        showSidebar: !isMobile,
        showStickyMobile: isMobile,
        inFeedDensity: getAdDensity,
      },
      tool: {
        showBelowTitle: false,
        showFirstParagraph: false,
        showMidContent: false,
        showEndContent: true,
        showSidebar: false,
        showStickyMobile: false,
      },
      home: {
        showBelowTitle: false,
        showFirstParagraph: false,
        showMidContent: false,
        showEndContent: true,
        showSidebar: false,
        showStickyMobile: isMobile,
      },
    };

    return configs[pageType] || configs.article;
  };

  return {
    showAds,
    isMobile,
    getAdDensity,
    trackAdImpression,
    getPageAdConfig,
    adSlots: AD_SLOTS, // Export ad slots for direct access
  };
}
